import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";

const C27UncertainityLevel = forwardRef(({ refetchMonitoringData, vesselMstId, shouldRefetch, levelOfUncertainities, monitoringMethods }, ref) => {
    const RowActions = {
        Edit: 0,
        Add: 1,
    };
    const [activeAction, setactiveAction] = useState(-1);
    const [levelData, setLevelData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [hasFetched, setHasFetched] = useState(false);

    const getC27prefillDatafromC21 = async () => {
        try {
            const response = await callApi({
                method: "GET",
                url: API_ROUTES.GET_C21_PREFILL_TO_C27(vesselMstId),
                data: {},
            });;
            if (response) {
                const levelData = response?.map((item) => ({
                    ...item,
                    value: item.value === 0 ? null : item.value ?? null,
                }));
                setLevelData(levelData || []);
            }
        } catch (error) {
            console.error("Error fetching C.2.7 data", error);
        }
    };

    useEffect(() => {
        if (shouldRefetch && !hasFetched) {
            getC27prefillDatafromC21();
            setHasFetched(true);
        }
    }, [shouldRefetch]);

    useEffect(() => {
        if (!shouldRefetch) {
            setHasFetched(false);
        }
    }, [shouldRefetch]);

    const getApproachesUsed = () => {
        return levelOfUncertainities?.map((item) => ({
            label: item.value,
            value: item.value
        }));
    };

    useImperativeHandle(ref, () => ({
        async submitAndCheck() {
            const { editingRow } = table.getState();
            if (!editingRow) return true;

            const editedValues = editingRow._valuesCache ?? {};
            const originalValues = editingRow.original ?? {};

            const isDirty = Object.keys(editedValues).some(
                (key) => editedValues[key] !== originalValues[key]
            );

            if (editedValues.value === 0) {
                setValidationErrors({
                    value: "Enter value more than zero",
                });
                return false;
            }

            if (editedValues.approachUsed === "Default value") {
                if (
                    editedValues.value === undefined ||
                    editedValues.value === null ||
                    editedValues.value.toString().trim() === ""
                ) {
                    editedValues.value = "7.5";
                }
            }
            if (
                editedValues.approachUsed === "Ship specific estimate" &&
                (editedValues.value === undefined ||
                    editedValues.value === null ||
                    editedValues.value.toString().trim() === "")
            ) {
                setValidationErrors({
                    value: "field is required",
                });
                return false;
            }
            if (isDirty) {
                const result = await handleSaveLevel(
                    { values: editedValues, table, row: editingRow },
                    true
                );

                if (!result) return false;
            }

            table.setEditingRow(null);
            setactiveAction(-1);
            setValidationErrors({});
            return true;
        }
    }));



    const handleFuelActions = async (table, row, action) => {
        if (activeAction === -1) {
            if (action === RowActions.Edit) {
                table.setEditingRow(row);
                setactiveAction(action);
            } else if (action === RowActions.Add) {
                table.setCreatingRow(true);
                setactiveAction(action);
            }
        }
    };

    const resetActiveRow = () => {
        setactiveAction(-1);
        setValidationErrors({});
    };

    const handleSaveLevel = async ({ values, table, row }, isAuto = false) => {
        if (values.value === 0) {
            setValidationErrors({ value: "Enter value more than zero" });
            return false;
        }

        if (values.approachUsed && !values.value?.toString().trim()) {
            setValidationErrors({ value: "field is required" });
            return false;
        }
        if (validationErrors?.value || validationErrors?.approachUsed) {
            return false;
        }

        const updatedData = levelData?.map((item, index) => {
            if (index === row.index) {
                return {
                    ...item,
                    emissionSourceRefNo: values.emissionSourceRefNo || "",
                    monitoringMethod: values.monitoringMethod || [],
                    emissionGuid: item.emissionGuid || "",
                    approachUsed: values.approachUsed || "",
                    value: values.value,
                };
            }
            return item;
        });

        const finalPayload = updatedData?.map((item) => ({
            ...item,
            c27LevelOfUncertainityId: 0,
            value: item.value === null ? 0 : Number(item.value)
        }));

        try {
            const c27response = await callApi({
                method: "POST",
                url: API_ROUTES.C27(vesselMstId),
                data: finalPayload,
            });

            if (c27response?.isSuccess) {
                emitToasterMsg(
                    "success",
                    "Table C.2.7",
                    isAuto ? "Level has been autosaved successfully" : "Level has been updated successfully"
                );
                table.setEditingRow(null);
                setactiveAction(-1);

                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }

                getC27prefillDatafromC21();
            } else {
                emitToasterMsg("error", "Table C.2.7", c27response?.errorMessage || "Save failed");
            }
        } catch (error) {
            emitToasterMsg("error", "Table C.2.7", error.errorMessage);
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: "emissionSourceRefNo",
                header: "Emissions source reference no",
                enableEditing: false,
            },
            {
                accessorKey: "monitoringMethod",
                header: "Monitoring method",
                enableEditing: false,
                Cell: ({ cell }) => {
                    const codeArr = cell.getValue();
                    if (!Array.isArray(codeArr)) return "";
                    return codeArr
                        .map((code) => {
                            const matched = monitoringMethods?.find((opt) => opt.code === code);
                            return matched ? matched.name : code;
                        })
                        .join(", ");
                },
            },
            {
                accessorKey: "approachUsed",
                header: "Approach used",
                editVariant: "select",
                editSelectOptions: getApproachesUsed(),
                muiEditTextFieldProps: ({ row }) => {
                    const monitoringMethod = row.original.monitoringMethod || [];
                    const multipleMonitoringMethods = monitoringMethod.length > 1;

                    if (multipleMonitoringMethods) {
                        row._valuesCache.approachUsed = "Ship specific estimate";
                    }
                    return {
                        select: true,
                        required: false,
                        value: row._valuesCache.approachUsed || "",
                        error: !!validationErrors?.approachUsed,
                        helperText: validationErrors?.approachUsed,
                        disabled: multipleMonitoringMethods,
                        onChange: (e) => {
                            const selectedValue = e.target.value;
                            row._valuesCache.approachUsed = selectedValue;
                            if (selectedValue === "Default value") {
                                row._valuesCache.value = "7.5";
                            } else {
                                row._valuesCache.value = "";
                            }
                            setValidationErrors((prev) => ({
                                ...prev,
                                approachUsed: undefined,
                            }));
                        },
                    };
                },
            },
            {
                accessorKey: "value",
                header: "Value (%)",
                muiEditTextFieldProps: ({ row }) => {
                    const approachUsed = row._valuesCache?.approachUsed;
                    const monitoringMethod = row._valuesCache?.monitoringMethod || [];
                    const isDefault = approachUsed === "Default value";
                    // Conditions for disabling
                    const shouldDisable = isDefault;
                    // Set 7.5 only if both approachUsed & monitoringMethod selected and approach is default
                    const value = approachUsed === "Default value" && monitoringMethod.length > 0 ? "7.5" : row._valuesCache?.value ?? "";
                    return {
                        required: true,
                        type: "number",
                        value: value,
                        disabled: shouldDisable,
                        error: !!validationErrors?.value,
                        helperText: validationErrors?.value,
                        type: "number",
                        onFocus: () =>
                            setValidationErrors((prev) => ({
                                ...prev,
                                value: undefined,
                            })),
                        onChange: (e) => {
                            const inputVal = e.target.value;
                            if (inputVal === "") {
                                row._valuesCache.value = null;
                                setValidationErrors((prev) => ({ ...prev, value: undefined }));
                                return;
                            }

                            if (!/^\d*\.?\d*$/.test(inputVal)) {
                                setValidationErrors((prev) => ({
                                    ...prev,
                                    value: "Please enter a valid number",
                                }));
                                return;
                            }

                            const numericValue = Number(inputVal);
                            if (numericValue === 0) {
                                setValidationErrors((prev) => ({
                                    ...prev,
                                    value: "Enter value more than zero",
                                }));
                                row._valuesCache.value = numericValue;
                                return;
                            }

                            setValidationErrors((prev) => ({
                                ...prev,
                                value: undefined,
                            }));

                            row._valuesCache.value = numericValue;
                        },

                    };
                },
            },
        ],
        [validationErrors]
    );

    const table = useMaterialReactTable({
        columns,
        data: levelData,
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: false,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enablePagination: false,
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "520px",
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
        },
        muiTableBodyCellProps: () => ({
            sx: {
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        }),
        muiTableBodyRowProps: {
            sx: {
                "&:not(:last-of-type)": {
                    borderBottom: "1px solid lightgray",
                },
                verticalAlign: "top",
            },
        },
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
            },
        },
        getRowId: (row) => row.emissionSourceRefNo,
        onEditingRowCancel: resetActiveRow,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowSave: handleSaveLevel,
        renderToolbarInternalActions: () => <></>,
        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: "flex", gap: "1rem" }}>
                <Tooltip title="Edit">
                    <IconButton
                        style={{ padding: "0px" }}
                        onClick={() => handleFuelActions(table, row, RowActions.Edit)}
                    >
                        <EditIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
    });

    return (
        <div className="c27-uncertainitylevel-table mt-1">
            <MaterialReactTable table={table} />
        </div>
    );
});

export default C27UncertainityLevel;